import { checkLDFlags } from '@/plugins/launchdarkly';
import { i18n } from '@/plugins/i18n';
import { queryClient } from '@/plugins/vueQuery';
import { FeatureFlags } from '@/types/featureFlags';
import type { AgentsEvent, EventAgentsBulkCreateFailed } from '@/types/websocket';
import { handleError } from '@/utils/errorHandling';
import { getAgentsQueryKey } from '@/utils/queries/agents';
import { addToast } from '@teamworksdev/vuetify';

const { t } = i18n.global;

export const handleAgentBulkCreateComplete = async (): Promise<void> => {
  try {
    const enabled = await checkLDFlags([FeatureFlags.AGENT_CSV_UPLOAD_ENABLED]);
    if (!enabled) return;

    addToast({
      type: 'success',
      title: t('toast.agentBulkUploadComplete.title'),
      description: t('toast.agentBulkUploadComplete.description'),
    });

    queryClient.invalidateQueries({ queryKey: getAgentsQueryKey() });
  } catch (error) {
    handleError(error);
  }
};

export const handleAgentBulkCreateFailed = async (event: EventAgentsBulkCreateFailed): Promise<void> => {
  try {
    const enabled = await checkLDFlags([FeatureFlags.AGENT_CSV_UPLOAD_ENABLED]);
    if (!enabled) return;

    const description = event.data.message
      ? t('toast.agentBulkUploadFailed.descriptionWithReason', { reason: event.data.message })
      : t('toast.agentBulkUploadFailed.description');

    addToast({
      type: 'error',
      title: t('toast.agentBulkUploadFailed.title'),
      description,
    });
  } catch (error) {
    handleError(error);
  }
};

export type AgentsEventHandlers = {
  [K in AgentsEvent['eventKey']]: (event: Extract<AgentsEvent, { eventKey: K }>) => void;
};

export const agentsEventHandlers: AgentsEventHandlers = {
  'agents.bulk.create.complete': handleAgentBulkCreateComplete,
  'agents.bulk.create.failed': handleAgentBulkCreateFailed,
};
